import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TwoFactorLogin from '../components/auth/TwoFactorLogin';
import Logo from '../components/Logo';
import { LogIn, UserPlus, AlertCircle } from 'lucide-react';
import logger from '../utils/logger';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [userId, setUserId] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  const { login, register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Redirect authenticated users away from login page
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Check for session expiry redirect
  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      setSessionExpired(true);
      // Clean up URL
      window.history.replaceState({}, '', '/login');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);

        // Check if 2FA is required
        if (result?.requiresTwoFactor) {
          setTwoFactorRequired(true);
          setUserId(result.userId);
          setLoading(false);
          return;
        }

        // Normal login (no 2FA)
        navigate('/dashboard');
      } else {
        await register(formData.name, formData.email, formData.password);
        navigate('/dashboard');
      }
    } catch (err) {
      logger.error('Login failed', { error: err, email: formData.email });
      const errorMessage = err.response?.data?.error
        || err.response?.data?.message
        || err.message
        || 'Authentication failed. Please check your credentials.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handle2FASuccess = () => {
    navigate('/dashboard');
  };

  const handle2FACancel = () => {
    setTwoFactorRequired(false);
    setUserId(null);
    setFormData({ ...formData, password: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Show 2FA screen if required
  if (twoFactorRequired && userId) {
    return (
      <TwoFactorLogin
        userId={userId}
        onSuccess={handle2FASuccess}
        onCancel={handle2FACancel}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f3f38] via-[#0d332d] to-[#0a2923] dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-dark-800 rounded-xl w-full max-w-md p-8 border border-transparent dark:border-dark-700 animate-scale-in" style={{ boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 8px 16px rgba(0,0,0,0.08)' }}>
        {/* Header with Brand Mark */}
        <div className="text-center mb-8 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
          <div className="flex justify-center mb-7">
            <div className="flex items-center gap-2.5">
              <Logo size="lg" variant="mini" />
              <div className="text-left leading-none">
                <div className="text-lg font-semibold tracking-wide text-brand-600 dark:text-white">
                  GABHAN ERP
                </div>
                <div className="mt-1.5 text-xs tracking-[0.14em] text-gray-500 dark:text-gray-400">
                  MANAGEMENT SYSTEM
                </div>
              </div>
            </div>
          </div>
          <p className="text-gray-700 dark:text-gray-300 font-medium text-[15px]">
            {isLogin ? 'Sign in to your account' : 'Create a new account'}
          </p>
        </div>

        {/* Session Expired Message - Only show when session actually expired and user is not authenticated */}
        {sessionExpired && !isAuthenticated && (
          <div
            className="mb-4 p-3 bg-amber-100 dark:bg-amber-900/20 border border-amber-400 dark:border-amber-700 text-amber-700 dark:text-amber-400 rounded-lg flex items-center gap-2 animate-fade-in"
            role="alert"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>Your session has expired. Please log in again.</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div
            className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg animate-fade-in feedback-error"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 animate-fade-in-up" style={{ animationDelay: '200ms' }} aria-label={isLogin ? 'Sign in form' : 'Sign up form'}>
          {!isLogin && (
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Full Name
              </label>
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required={!isLogin}
                className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-colors ${
                  error
                    ? 'border-red-400 dark:border-red-600 focus:ring-0'
                    : 'border-gray-300 dark:border-dark-600 focus:ring-2 focus:ring-brand-500 focus:border-transparent'
                }`}
                placeholder="John Doe"
                aria-required="true"
                aria-invalid={error ? 'true' : 'false'}
              />
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-colors ${
                error
                  ? 'border-red-400 dark:border-red-600 focus:ring-0'
                  : 'border-gray-300 dark:border-dark-600 focus:ring-2 focus:ring-brand-500 focus:border-transparent'
              }`}
              placeholder="you@example.com"
              autoComplete="email"
              aria-required="true"
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? 'login-error' : undefined}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password
              </label>
              {isLogin && (
                <Link
                  to="/forgot-password"
                  className="text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-medium"
                  aria-label="Reset your password"
                >
                  Forgot password?
                </Link>
              )}
            </div>
            <input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
              className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-colors ${
                error
                  ? 'border-red-400 dark:border-red-600 focus:ring-0'
                  : 'border-gray-300 dark:border-dark-600 focus:ring-2 focus:ring-brand-500 focus:border-transparent'
              }`}
              placeholder="••••••••"
              autoComplete={isLogin ? 'current-password' : 'new-password'}
              aria-required="true"
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby="password-requirements"
            />
            <p id="password-requirements" className="sr-only">
              Password must be at least 6 characters long
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-500 hover:bg-brand-600 dark:bg-brand-500 dark:hover:bg-brand-600 text-white font-semibold py-3.5 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 btn-press hover:shadow-lg hover:shadow-brand-500/25"
            aria-busy={loading}
            aria-live="polite"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                <span className="sr-only">Processing...</span>
              </>
            ) : isLogin ? (
              <>
                <LogIn size={18} aria-hidden="true" />
                Sign In
              </>
            ) : (
              <>
                <UserPlus size={18} aria-hidden="true" />
                Create Account
              </>
            )}
          </button>
        </form>

        {/* Toggle Login/Register */}
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => {
              setIsLogin(!isLogin);
              setError('');
              setFormData({ name: '', email: '', password: '' });
            }}
            className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 text-sm font-medium"
            aria-label={isLogin ? 'Switch to sign up form' : 'Switch to sign in form'}
          >
            {isLogin
              ? "Don't have an account? Sign up"
              : 'Already have an account? Sign in'}
          </button>
        </div>
      </div>

      {/* Legal footer */}
      <div className="absolute bottom-4 left-0 right-0 text-center text-xs text-gray-400">
        <Link to="/privacy" className="hover:text-white">Privacy</Link>
        <span className="mx-2">&middot;</span>
        <Link to="/terms" className="hover:text-white">Terms</Link>
      </div>
    </div>
  );
};

export default Login;
